/**
 * DECISIONS 402 (397, the owner: "base starts with ~hundreds of magazines and bandages, it all gets distributed as needed,
 * and that's it for the game"; his picks: respawns draw from the stock, the issue stays 5 magazines and 2 bandages, the
 * stock physical), with GameConfig.finiteSupply: each side's magazines and bandages a finite stock, on 65.23's pattern
 * (core/grenadeStock.ts).
 * - **The stock** (`state.supplyStocks`): the side's motor pool's containers — the magazines in its ammo container, the
 *   bandages in its medical one — sized SUPPLY_STOCK.share of an issue for every life the side has, its units and its
 *   tickets (408; SUPPLY_STOCK.refills issues a unit for a side whose arrivals are unlimited). A container lost
 *   (its health spent) takes what it holds with it.
 * - **A respawn's issue** comes out of it: the long gun's magazine and spares (in whole magazines) and the bandages, as far
 *   as the stock goes — a late arrival comes short, or with an empty gun. The sidearm's comes with the person, as before.
 *   A support's case is filled from it too.
 * - **Where it stands** (`placeStock`, once the pools stand): each container in its slot of its side's motor pool (the look's
 *   own fill, core/motorPool.ts), else at the side's base dump. A blast within its reach wears it (`blastStock`); spent, it is
 *   lost with what it holds.
 * - **The base dumps** (`supply.fed`) are kept at their SUPPLY stock from it, so a crate's hand-out (core/supply.ts) is the
 *   stock's; the point crates' and the FOBs' refills, and a convoy's load, come out of it (`chargeRefills`, `takeStock`).
 * Pure; with the switch off no side has a stock and nothing here does anything.
 */
import type { CarrierExplosives, GameState, GroundWeaponKind, PlayerState, StockContainer, StructureState, Vec3 } from '../types';
import type { ExplosionClass } from '../constants/explosions';
import { BANDAGE, SUPPLY, SUPPORT, GROUND_WEAPONS, AMMO_RESERVE } from '../constants/action';
import { SUPPLY_STOCK } from '../constants/logistics';
import { EXPLOSIONS } from '../constants/explosions';
import { WEAPONS } from '../constants/weapons';
import { primaryOf, sidearmOf } from './weapons';
import { isSupport } from './supportRole';
import { crateTeam } from './supply';
import { structureJob, structureTeam } from './body';
import { MOTOR_POOL, motorPoolLoads, fillMotorPool, planCentre } from './motorPool';
import { takeStock } from './stockTake';
import { structureFrame } from './hits';
import { explosionBlast } from './explosions';
import { getSurfaceRadius } from './surface';
import { arcDistance } from './gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { vecAdd, vecLength, vecNormalize, vecScale, vecSub } from '../math';

export type StockEvent =
  | { kind: 'stockIssued'; unitId: number; magazines: number; bandages: number; short: boolean }
  /** A side's container spent by a blast — lost with what it held. */
  | { kind: 'stockLost'; team: number; packing: StockContainer['packing']; magazines: number; bandages: number; explosives: number }
  /** DECISIONS 406: a carrier loaded or refilled at its base out of its side's stock. */
  | { kind: 'carrierLoaded'; structureId: number; team: number; bandages: number; magazines: number; explosives: number };

/** A gun's issue in its magazines: the one in it and the spares (the rifle's 30 + 120: 5; the pistol's 12 + 24: 3). */
function gunIssue(kind: GroundWeaponKind): number {
  const m = GROUND_WEAPONS[kind].magazine;
  return Math.ceil((m + (AMMO_RESERVE[kind] ?? 0)) / m);
}

/** DECISIONS 406 (bullets): the guns a life's bullet issue fills — its long gun, then its sidearm. */
function bulletGuns(p: Pick<PlayerState, 'primary' | 'sidearm'>): GroundWeaponKind[] {
  return [primaryOf(p), sidearmOf(p)];
}

/** A life's bullet issue, in magazines: its long gun's and its sidearm's (the rifleman's 5 + 3). */
export function issueMagazines(p: Pick<PlayerState, 'primary' | 'sidearm'>): number {
  return bulletGuns(p).reduce((a, k) => a + gunIssue(k), 0);
}

/**
 * DECISIONS 406 (the owner: "small arms should be split into bullet small arms magazines / restock, and explosive small arms
 * magazines / restock"): a side's explosive small arms left — its grenades, the 40 mm's rounds and the rockets in its
 * ledgers (core/grenadeStock.ts, gl40Stock.ts, rocketStock.ts, each its own issue and restock), held in its ammo container.
 */
export function explosivesLeft(state: Pick<GameState, 'grenadeStocks' | 'gl40Stocks' | 'rocketStocks'>, team: number): number {
  const g = state.grenadeStocks?.[team];
  return (g ? g.he + g.smoke : 0) + (state.gl40Stocks?.[team] ?? 0) + (state.rocketStocks?.[team] ?? 0);
}

/** The side's explosive ledgers emptied — its ammo container lost. */
function loseExplosives(state: GameState, team: number): GameState {
  return {
    ...state,
    ...(state.grenadeStocks?.[team] ? { grenadeStocks: { ...state.grenadeStocks, [team]: { he: 0, smoke: 0 } } } : {}),
    ...(state.gl40Stocks?.[team] !== undefined ? { gl40Stocks: { ...state.gl40Stocks, [team]: 0 } } : {}),
    ...(state.rocketStocks?.[team] !== undefined ? { rocketStocks: { ...state.rocketStocks, [team]: 0 } } : {}),
  };
}

/**
 * The setup's sizing (408), once its tickets are set: each side's containers hold SUPPLY_STOCK.share of a mean issue for
 * every life it has (its units and its tickets; no respawn layer, its units alone), or `refills` issues a unit when its
 * arrivals are unlimited; its dumps are fed from them.
 */
export function withSupplyStocks(state: GameState): GameState {
  if (!state.config.finiteSupply) return state;
  const respawn = state.modeState.respawn;
  const mags: Record<number, number> = {}, bands: Record<number, number> = {}, units: Record<number, number> = {};
  for (const p of state.players) {
    mags[p.teamIndex] = (mags[p.teamIndex] ?? 0) + issueMagazines(p);
    bands[p.teamIndex] = (bands[p.teamIndex] ?? 0) + BANDAGE.carried;
    units[p.teamIndex] = (units[p.teamIndex] ?? 0) + 1;
  }
  for (const t of Object.keys(units).map(Number)) {
    const scale = respawn?.unlimited?.includes(t) ? SUPPLY_STOCK.refills
      : ((units[t] + (respawn?.tickets[t] ?? 0)) / units[t]) * SUPPLY_STOCK.share;   // (the mean issue × the lives × the share)
    mags[t] *= scale;
    bands[t] *= scale;
  }
  const supplyStocks: Record<number, StockContainer[]> = {};
  for (const t of Object.keys(mags).map(Number).sort((a, b) => a - b)) {
    supplyStocks[t] = [
      { packing: 'ammo', magazines: Math.floor(mags[t]), bandages: 0, health: SUPPLY_STOCK.containerHealth },
      { packing: 'medical', magazines: 0, bandages: Math.floor(bands[t]), health: SUPPLY_STOCK.containerHealth },
    ];
  }
  const structures = state.structures.map(s => (baseDump(s) ? { ...s, supply: { ...s.supply!, fed: true as const } } : s));
  return { ...state, structures, supplyStocks };
}

/** A side's own dump at the setup: a team crate with magazines or bandages (not a point's, a drop, a FOB's or a case). */
function baseDump(s: StructureState): boolean {
  return !!s.supply && s.supply.site === undefined && !s.supply.dropped && s.type !== 'fob' && s.type !== 'ammoCase'
    && !!structureJob(s).supply && s.supply.magazines + s.supply.bandages > 0;
}

/** What a side's standing containers hold. */
export function stockLeft(state: Pick<GameState, 'supplyStocks'>, team: number): { magazines: number; bandages: number } {
  let magazines = 0, bandages = 0;
  for (const c of state.supplyStocks?.[team] ?? []) if (c.health > 0) { magazines += c.magazines; bandages += c.bandages; }
  return { magazines, bandages };
}

export { takeStock };   // (in core/stockTake.ts, off the motor pool's import cycle)

/**
 * A respawn's issue from its side's stock: the long gun's magazine and spares in whole magazines (the gun filled first),
 * the bandages, a support's case — each as far as the stock goes. The event says what came and whether it came short.
 */
export function issueFromStock(state: GameState, unitId: number): { state: GameState; event: StockEvent | null } {
  const p = state.players.find(x => x.id === unitId);
  if (!p || !state.supplyStocks?.[p.teamIndex]) return { state, event: null };
  const want = issueMagazines(p);
  const caseWant = isSupport(state, p) ? SUPPORT.case : 0;
  const took = takeStock(state, p.teamIndex, { magazines: want + caseWant, bandages: BANDAGE.carried });
  const forGun = Math.min(want, took.magazines), forCase = took.magazines - forGun;
  // (406: the long gun's issue first, then the sidearm's — each its gun filled, then its spares)
  const magazines = { ...(p.magazines ?? {}) }, reserves = { ...(p.reserves ?? {}) };
  let left = forGun;
  for (const kind of bulletGuns(p)) {
    const m = GROUND_WEAPONS[kind].magazine, n = Math.min(left, gunIssue(kind));
    left -= n;
    const rounds = n * m, inGun = Math.min(m, rounds);
    if (inGun >= m) delete magazines[kind]; else magazines[kind] = inGun;
    reserves[kind] = Math.min(rounds - inGun, AMMO_RESERVE[kind] ?? 0);
  }
  const next: PlayerState = {
    ...p, magazines: Object.keys(magazines).length ? magazines : undefined, reserves, bandages: took.bandages,
    ...(caseWant ? { ammoCase: forCase } : {}),
  };
  const short = forGun < want || took.bandages < BANDAGE.carried || forCase < caseWant;
  return {
    state: { ...took.state, players: took.state.players.map(x => (x.id === unitId ? next : x)) },
    event: { kind: 'stockIssued', unitId, magazines: forGun, bandages: took.bandages, short },
  };
}

/** The base dumps topped back up to their SUPPLY stock from their side's containers. Null when nothing moved. */
export function restockDumps(state: GameState): GameState | null {
  if (!state.supplyStocks) return null;
  let next = state, structures: StructureState[] | null = null;
  state.structures.forEach((s, i) => {
    if (!s.supply?.fed || s.health <= 0) return;
    const want = { magazines: SUPPLY.magazines - s.supply.magazines, bandages: SUPPLY.bandages - s.supply.bandages };
    if (want.magazines <= 0 && want.bandages <= 0) return;
    const took = takeStock(next, s.supply.teamIndex, want);
    if (took.magazines + took.bandages <= 0) return;
    next = took.state;
    (structures ??= state.structures.slice())[i] = { ...s, supply: { ...s.supply, magazines: s.supply.magazines + took.magazines, bandages: s.supply.bandages + took.bandages } };
  });
  return structures ? { ...next, structures } : null;
}

/**
 * A refill's gains (the point crates' trickle, the FOBs'): what each crate gained over `before` comes out of the stock of the
 * side it serves (a point's holder), as far as that goes — a crate whose side is out gains nothing. The state with the
 * structures and stocks after.
 */
export function chargeRefills(state: GameState, after: StructureState[]): GameState {
  if (!state.supplyStocks) return { ...state, structures: after };
  let next: GameState = state;
  const structures = after.map((s, i) => {
    const was = state.structures[i];
    if (!s.supply || !was?.supply || was.id !== s.id) return s;
    const dm = s.supply.magazines - was.supply.magazines, db = s.supply.bandages - was.supply.bandages;
    if (dm <= 0 && db <= 0) return s;
    const team = crateTeam(state, s);
    if (team === null) return was;
    const took = takeStock(next, team, { magazines: Math.max(0, dm), bandages: Math.max(0, db) });
    next = took.state;
    return { ...s, supply: { ...s.supply, magazines: was.supply.magazines + took.magazines, bandages: was.supply.bandages + took.bandages } };
  });
  return { ...next, structures };
}

/**
 * Each side's containers placed, once its pool stands (the setup's last placing): in the pool's slot its container fills (the
 * look's own fill), else at its base dump; a side with neither leaves them unplaced (no blast reaches them).
 */
export function placeStock(state: GameState): GameState {
  if (!state.supplyStocks) return state;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const supplyStocks: Record<number, StockContainer[]> = {};
  for (const [key, stock] of Object.entries(state.supplyStocks)) {
    const team = Number(key);
    const pool = state.structures.find(s => s.type === 'motorPool' && s.health > 0 && structureTeam(state, s) === team);
    const dump = state.structures.find(s => s.supply?.fed && s.supply.teamIndex === team);
    const stacks = pool ? fillMotorPool(MOTOR_POOL, motorPoolLoads(state, team)).stacks : [];
    supplyStocks[team] = stock.map(c => {
      const st = stacks.find(x => 'container' in x.load && x.load.container === c.packing);
      if (pool && st) {
        const sl = MOTOR_POOL.slots[st.slot], at = planCentre(MOTOR_POOL, sl.i, sl.j, 3, 1);   // (people: a slot three cells along x)
        const up = vecNormalize(pool.surfaceDir), { tx, tz } = structureFrame(pool, up);
        const dir = vecNormalize(vecAdd(up, vecScale(vecAdd(vecScale(tx, at.x), vecScale(tz, at.z)), person / state.planets[pool.planetIndex].radius)));
        return { ...c, at: { planetIndex: pool.planetIndex, dir } };
      }
      return dump ? { ...c, at: { planetIndex: dump.planetIndex, dir: vecNormalize(dump.surfaceDir) } } : c;
    });
  }
  return { ...state, supplyStocks };
}

/** A burst's damage circle in world units: the person family's blast, the planet family's weapon (null: none). */
function burstOf(state: GameState, cls: ExplosionClass): { radius: number; damage: number } | null {
  const def = EXPLOSIONS[cls];
  if (def.blast) return explosionBlast(state, cls);
  const w = def.weapon ? WEAPONS[def.weapon] : undefined;
  return w ? { radius: w.blastRadius, damage: w.damage } : null;
}

/**
 * This tick's blasts on the containers: each standing container within a blast's circle takes its damage, falling off to
 * its edge; one brought to 0 is lost with what it held (an event each). The same state when none was reached.
 */
export function blastStock(state: GameState, blasts: ReadonlyArray<{ point: Vec3; planetIndex: number; explosion: ExplosionClass }>): { state: GameState; events: StockEvent[] } {
  const events: StockEvent[] = [];
  if (!state.supplyStocks || !blasts.length) return { state, events };
  let supplyStocks: Record<number, StockContainer[]> | null = null;
  const explosivesLost: number[] = [];
  for (const [key, stock] of Object.entries(state.supplyStocks)) {
    let next: StockContainer[] | null = null;
    stock.forEach((c, i) => {
      if (c.health <= 0 || !c.at) return;
      const planet = state.planets[c.at.planetIndex], spot = vecAdd(planet.position, vecScale(c.at.dir, getSurfaceRadius(planet, c.at.dir)));
      let health = c.health;
      for (const b of blasts) {
        if (b.planetIndex !== c.at.planetIndex) continue;
        const burst = burstOf(state, b.explosion);
        if (!burst) continue;
        const d = vecLength(vecSub(b.point, spot));
        if (d < burst.radius) health -= burst.damage * (1 - d / burst.radius);
      }
      if (health === c.health) return;
      const lost = health <= 0;
      (next ??= stock.slice())[i] = { ...c, health: Math.max(0, health) };
      if (lost) {
        const explosives = c.packing === 'ammo' ? explosivesLeft(state, Number(key)) : 0;   // (406: the explosive small arms go with the ammo container)
        if (c.packing === 'ammo') explosivesLost.push(Number(key));
        events.push({ kind: 'stockLost', team: Number(key), packing: c.packing, magazines: c.magazines, bandages: c.bandages, explosives });
      }
    });
    if (next) (supplyStocks ??= { ...state.supplyStocks })[Number(key)] = next;
  }
  let out = supplyStocks ? { ...state, supplyStocks } : state;
  for (const t of explosivesLost) out = loseExplosives(out, t);
  return { state: out, events };
}

/** DECISIONS 406: the explosive kinds a carrier loads, each out of its side's ledger. */
const EXPLOSIVE_KINDS: ReadonlyArray<keyof CarrierExplosives> = ['he', 'smoke', 'gl40', 'rocket'];

/**
 * DECISIONS 406 (the owner: "APCs and M113s should carry some bandages and basic restock for small arms"): every standing APC
 * and M113 of a side with a stock, within SUPPLY_STOCK.carrier.reachPeople of its side's base dump, loaded (a first time) or
 * topped back up to its load out of that stock — bandages and bullet magazines from the containers, each explosive kind
 * from its ledger, as far as each goes. Null when nothing moved.
 */
export function loadCarriers(state: GameState): { state: GameState; events: StockEvent[] } | null {
  if (!state.supplyStocks) return null;
  const C = SUPPLY_STOCK.carrier, reach = C.reachPeople * PERSON_BODY_HEIGHT * personSetOf(state).k;
  const kinds: ReadonlySet<string> = new Set(C.kinds);
  let next = state, structures: StructureState[] | null = null;
  const events: StockEvent[] = [];
  state.structures.forEach((s, i) => {
    if (!kinds.has(s.type) || s.health <= 0) return;
    const team = s.supply?.teamIndex ?? structureTeam(state, s);
    if (team === null || !state.supplyStocks![team]) return;
    const R = state.planets[s.planetIndex].radius;
    const home = state.structures.some(d => d.supply?.fed && d.supply.teamIndex === team && d.health > 0 && d.planetIndex === s.planetIndex && arcDistance(d.surfaceDir, s.surfaceDir, R) <= reach);
    if (!home) return;
    const was = s.supply?.carrier ? s.supply : { teamIndex: team, bandages: 0, magazines: 0, carrier: true as const, explosives: { he: 0, smoke: 0, gl40: 0, rocket: 0 } };
    const took = takeStock(next, team, { magazines: C.magazines - was.magazines, bandages: C.bandages - was.bandages });
    next = took.state;
    const explosives = { ...(was.explosives ?? { he: 0, smoke: 0, gl40: 0, rocket: 0 }) };
    let gotX = 0;
    for (const k of EXPLOSIVE_KINDS) {
      const want = C.explosives[k] - explosives[k];
      if (want <= 0) continue;
      const have = k === 'he' || k === 'smoke' ? next.grenadeStocks?.[team]?.[k] ?? 0 : k === 'gl40' ? next.gl40Stocks?.[team] ?? 0 : next.rocketStocks?.[team] ?? 0;
      const n = Math.min(want, have);
      if (n <= 0) continue;
      explosives[k] += n; gotX += n;
      if (k === 'he' || k === 'smoke') next = { ...next, grenadeStocks: { ...next.grenadeStocks, [team]: { ...next.grenadeStocks![team], [k]: have - n } } };
      else if (k === 'gl40') next = { ...next, gl40Stocks: { ...next.gl40Stocks, [team]: have - n } };
      else next = { ...next, rocketStocks: { ...next.rocketStocks, [team]: have - n } };
    }
    if (s.supply?.carrier && took.magazines + took.bandages + gotX <= 0) return;
    (structures ??= state.structures.slice())[i] = { ...s, supply: { ...was, magazines: was.magazines + took.magazines, bandages: was.bandages + took.bandages, explosives } };
    if (took.magazines + took.bandages + gotX > 0) events.push({ kind: 'carrierLoaded', structureId: s.id, team, bandages: took.bandages, magazines: took.magazines, explosives: gotX });
  });
  return structures ? { state: { ...next, structures }, events } : null;
}
